/**
 * ContactTab - Contact section management
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const DEFAULT_BLOCKS = [
  { id: 'live_chat', icon: 'MessageSquare', title: 'Live Chat', subtitle: 'Chat with us now', bgColor: '#007a33', bgColorDark: '#007a33' },
  { id: 'whatsapp', icon: 'Phone', title: 'WhatsApp', subtitle: 'Message us on WhatsApp', bgColor: '#25D366', bgColorDark: '#25D366' },
  { id: 'email', icon: 'Mail', title: 'Email', subtitle: 'Send us an email', bgColor: '#1a472a', bgColorDark: '#1a472a' },
];

export function ContactTab({ content, setContent }) {
  const blocks = content.contact?.blocks || DEFAULT_BLOCKS;

  const updateContact = (field, value) => {
    setContent(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
  };

  const updateBlock = (index, field, value) => {
    const updated = blocks.map((b, i) => i === index ? { ...b, [field]: value } : b);
    updateContact('blocks', updated);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Contact Section</CardTitle>
          <CardDescription>Section header and contact blocks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Badge Text</Label>
              <Input value={content.contact?.badge || ''} onChange={(e) => updateContact('badge', e.target.value)} placeholder="e.g., GET IN TOUCH" data-testid="contact-badge" />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={content.contact?.title || ''} onChange={(e) => updateContact('title', e.target.value)} placeholder="e.g., Contact Us" data-testid="contact-title" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={content.contact?.subtitle || ''} onChange={(e) => updateContact('subtitle', e.target.value)} placeholder="e.g., We're here to help" data-testid="contact-subtitle" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title Font Colour</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={content.contact?.titleColor || '#111827'} onChange={(e) => updateContact('titleColor', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid="contact-title-color" />
                <Input value={content.contact?.titleColor || '#111827'} onChange={(e) => updateContact('titleColor', e.target.value)} className="w-28 h-8 text-xs font-mono" />
              </div>
            </div>
            <div>
              <Label>Subtitle Font Colour</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={content.contact?.subtitleColor || '#6b7280'} onChange={(e) => updateContact('subtitleColor', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid="contact-subtitle-color" />
                <Input value={content.contact?.subtitleColor || '#6b7280'} onChange={(e) => updateContact('subtitleColor', e.target.value)} className="w-28 h-8 text-xs font-mono" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {blocks.map((block, index) => (
        <Card key={block.id || index}>
          <CardHeader>
            <CardTitle className="text-base">{block.title || `Block ${index + 1}`}{index === 0 ? ' (Full Width)' : ' (Half Width)'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input value={block.title || ''} onChange={(e) => updateBlock(index, 'title', e.target.value)} data-testid={`contact-block-title-${index}`} />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={block.subtitle || ''} onChange={(e) => updateBlock(index, 'subtitle', e.target.value)} data-testid={`contact-block-subtitle-${index}`} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={block.description || ''} onChange={(e) => updateBlock(index, 'description', e.target.value)} placeholder="Optional longer description" data-testid={`contact-block-desc-${index}`} />
            </div>
            {block.id === 'whatsapp' && (
              <div>
                <Label>WhatsApp Phone Number</Label>
                <Input value={block.linkValue || ''} onChange={(e) => updateBlock(index, 'linkValue', e.target.value)} placeholder="e.g., 447123456789 (country code, no +)" data-testid="contact-whatsapp-number" />
              </div>
            )}
            {block.id === 'email' && (
              <div>
                <Label>Email Address</Label>
                <Input value={block.linkValue || ''} onChange={(e) => updateBlock(index, 'linkValue', e.target.value)} placeholder="e.g., support@example.com" data-testid="contact-email-address" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Icon</Label>
                <select
                  value={block.icon || 'MessageSquare'}
                  onChange={(e) => updateBlock(index, 'icon', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid={`contact-block-icon-${index}`}
                >
                  <option value="MessageSquare">Chat Bubble</option>
                  <option value="Phone">Phone</option>
                  <option value="Mail">Email</option>
                </select>
              </div>
              <div>
                <Label>Background (Light)</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={block.bgColor || '#007a33'} onChange={(e) => updateBlock(index, 'bgColor', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid={`contact-block-color-${index}`} />
                  <Input value={block.bgColor || '#007a33'} onChange={(e) => updateBlock(index, 'bgColor', e.target.value)} className="w-28 h-8 text-xs font-mono" />
                </div>
              </div>
              <div>
                <Label>Background (Dark)</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={block.bgColorDark || block.bgColor || '#007a33'} onChange={(e) => updateBlock(index, 'bgColorDark', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" data-testid={`contact-block-color-dark-${index}`} />
                  <Input value={block.bgColorDark || block.bgColor || '#007a33'} onChange={(e) => updateBlock(index, 'bgColorDark', e.target.value)} className="w-28 h-8 text-xs font-mono" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
